import { useState } from "react"
import { AUTH_DISPLAY, AUTH_MONO } from "./authTheme"

type MaskType = "phone" | "code"

type MaskConfig = {
    template: string
    maxDigits: number
    normalize: (v: string) => string
}

type MaskInputProps = {
    digits: string
    setDigits: React.Dispatch<React.SetStateAction<string>>
    type: MaskType
    error?: boolean
    setError?: React.Dispatch<React.SetStateAction<boolean>>
    /** Центрировать маску (экран телефона) */
    centered?: boolean
    autoFocus?: boolean
}

export const masks: Record<MaskType, MaskConfig> = {
    phone: {
        template: "+7 (___) ___-__-__",
        maxDigits: 10,
        normalize: v => v.replace(/\D/g, "").replace(/^7|^8/, "").slice(0, 10),
    },

    code: {
        template: "____",
        maxDigits: 4,
        normalize: v => v.replace(/\D/g, "").slice(0, 10),
    },
}

export default function MaskInput({ digits, setDigits, type, error, setError, centered, autoFocus }: MaskInputProps) {
    const [focused, setFocused] = useState(false)
    const { template, maxDigits, normalize } = masks[type]
    const isPhone = type === "phone"
    const phoneComplete = isPhone && digits.length >= maxDigits
    // После полного номера без синего фокуса/мигания — иначе последняя цифра «пропадает»
    const isActive = focused && isPhone && !phoneComplete

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (error && setError) setError(false)
        setDigits(normalize(e.target.value))
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        setDigits(normalize(e.clipboardData.getData("text")))
    }

    const caretIndex = digits.length < maxDigits ? digits.length : -1

    const renderPhone = () => {
        let i = 0

        return template.split("").map((char, index) => {
            if (char !== "_") {
                return (
                    <span key={index} className={isActive ? "text-[#349be6]" : "text-white"}>
                        {char}
                    </span>
                )
            }

            const value = digits[i] ?? "_"
            const isCaret = caretIndex >= 0 && i === caretIndex && focused
            i++

            const filled = value !== "_"

            return (
                <span
                    key={index}
                    className={`inline-block min-w-[0.5em] text-center ${
                        isCaret
                            ? "animate-[blink_1.2s_ease-in-out_infinite] text-[#349be6]"
                            : filled
                              ? "text-white"
                              : isActive
                                ? "text-white/45"
                                : "text-white/30"
                    }`}>
                    {value}
                </span>
            )
        })
    }

    const renderCode = () => {
        return Array.from({ length: maxDigits }).map((_, i) => {
            const value = digits[i] ?? ""
            const isActive = caretIndex >= 0 && i === caretIndex && focused

            return (
                <span
                    key={i}
                    className={`flex h-[70px] w-[60px] items-center justify-center rounded-[18px] border p-2 text-[32px] leading-none ${
                        error
                            ? "border-[#e4206b] bg-[#e4206b]/10 text-[#e4206b]"
                            : "border-white/15 bg-white/[0.06] text-white"
                    } ${isActive ? "animate-[blink-border_1.2s_ease-in-out_infinite]" : ""}`}
                    style={{ fontFamily: AUTH_MONO, fontWeight: 700 }}>
                    {value}
                </span>
            )
        })
    }

    const isCode = type === "code"

    const frameClass = isPhone ? "overflow-visible px-4 py-5 sm:px-6 sm:py-6" : isCode ? "overflow-visible px-2 py-2" : "overflow-visible px-4 py-4"

    const phoneGlowStyle = isActive
        ? { textShadow: "0 0 20px rgba(52,155,230,0.55), 0 0 40px rgba(52,155,230,0.2)" }
        : undefined

    const phoneFontClass = isActive
        ? "text-[clamp(28px,9cqi,34px)] tracking-tight"
        : "text-[clamp(26px,8.5cqi,32px)] tracking-tight"

    return (
        <div
            className={[
                "@container",
                centered || isCode || isPhone ? "mx-auto w-full max-w-full" : "w-full",
                frameClass,
            ].join(" ")}>
            <div className="relative flex justify-center" onClick={() => setFocused(true)}>
                {isPhone && (
                    <div
                        aria-hidden
                        className={`pointer-events-none absolute left-1/2 top-1/2 -z-10 w-[130%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transition-opacity duration-300 ${
                            isActive ? "opacity-100" : "opacity-0"
                        }`}
                        style={{
                            height: "3.5em",
                            background:
                                "radial-gradient(ellipse at center, rgba(52,155,230,0.22) 0%, rgba(52,155,230,0.08) 45%, transparent 72%)",
                        }}
                    />
                )}
                <div
                    className={[
                        "relative cursor-text whitespace-nowrap leading-none transition-[font-size,color] duration-200",
                        isPhone && phoneFontClass,
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    style={{
                        fontFamily: AUTH_DISPLAY,
                        fontWeight: 700,
                        ...phoneGlowStyle,
                    }}>
            <style>
                {`
                @keyframes blink {
                    0% { opacity: 1 }
                    50% { opacity: 0 }
                    100% { opacity: 1 }
                }
                @keyframes blink-border {
                    0% { border-color: rgba(52, 155, 230, 0.35) }
                    50% { border-color: rgba(52, 155, 230, 1) }
                    100% { border-color: rgba(52, 155, 230, 0.35) }
                }`}
            </style>
            <input
                maxLength={maxDigits}
                type="tel"
                value={digits}
                autoFocus={autoFocus}
                onChange={handleChange}
                onPaste={handlePaste}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                inputMode="numeric"
                className="absolute inset-0 caret-transparent opacity-0"
            />
            <div className={`pointer-events-none inline-flex whitespace-nowrap ${isCode ? "gap-3 sm:gap-4" : ""}`}>
                {isPhone ? renderPhone() : renderCode()}
            </div>
                </div>
            </div>
        </div>
    )
}
